import React, { useEffect, useRef, useState, memo } from 'react';
import { Route, Switch } from 'react-router-dom';
import isElectron from 'is-electron';

import homeClient from 'flow/api-clients/home-client';
import { useAuthService } from 'flow/authservice/useAuthService';
import ReleaseNotes from 'flow/components/ReleaseNotes';
import CallRecordingPage from 'flow/route-components/CallRecordingPage';
import CentralSwitch from 'flow/route-components/CentralSwitch';
import UaePassWebCallback from 'flow/route-components/UaePassWebCallback';
import CtiAuthentication from 'flow/route-components/cti-authentication';
import CtiSSO from 'flow/route-components/cti-sso';
import InitialUpdate from 'flow/route-components/electron/InitialUpdate';
import Main from 'flow/route-components/Main';
import SignIn from 'flow/route-components/sign-in';
import SipTest from 'flow/route-components/SipTest';
import SocialIntegrationOauthLanding from 'flow/route-components/SocialIntegrationOauthLanding';
import SocialIntegrationCallback from 'flow/route-components/SocialIntegrationCallback';
import { TeamsAuthenticationForm } from 'flow/route-components/TeamsAuthentication';
import AuthWithToken from 'flow/routes/auth-with-token';
import TrackingConsentModal from 'flow/components/settings/TrackingConsentModal';
import ConsentReminderBanner from 'flow/components/ConsentReminderBanner';
import { selectAnalyticsConsent } from 'flow/selectors/app-selectors';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { routes } from 'flow/types/routes';
import { useUserRegionData } from 'flow/hooks/region-lookup/useUserRegionData';
import { isUaePassEnabled } from 'flow/constants/uae-pass/uae-pass-constants';
import { getBrandKey } from 'flow/brands/brands';
import { isEtisalatBrand, isClaroBrand } from 'flow/utils/brand-utils';

const MainRoutes = () => {
    let defaultRouteComponent = Main;
    const needsInitialUpdateRef = useRef(true);
    const { token } = useAuthService();
    const { updateRegionData, isRegionCheckEnabled } = useUserRegionData();
    const { embedded } = useAppContext();
    const analyticsConsent = useAppSelector(selectAnalyticsConsent);
    const [consentDismissed, setConsentDismissed] = useState(false);
    const isSuccessLogin = useAppSelector(({ app }) => {
        const loginData = app.get('loginData');
        if (!loginData) return false;
        return (
            loginData.get('waiting') === false &&
            loginData.get('error') === false
        );
    });

    const autoLoginTokenId = useAppSelector((state) =>
        state.app.getIn(['autoLogin', 'autoLoginAccessTokenId']),
    );

    if (isElectron() && !isSuccessLogin && !autoLoginTokenId) {
        if (process.env.NO_UPDATE !== 'true' && needsInitialUpdateRef.current) {
            defaultRouteComponent = InitialUpdate;
            needsInitialUpdateRef.current = false;
        } else {
            defaultRouteComponent = SignIn;
        }
    }

    useEffect(() => {
        if (token) {
            homeClient.addAuthServiceToken(token);
        }
    }, [token]);

    useEffect(() => {
        if (isRegionCheckEnabled && isSuccessLogin) {
            updateRegionData({
                successLoginCompleted: true,
                lastUpdatedTime: Date.now(),
            });
        }
    }, [isRegionCheckEnabled, isSuccessLogin, updateRegionData]);

    const handleConsentClose = () => setConsentDismissed(true);

    const hideConsentUI =
        isEtisalatBrand(getBrandKey()) || isClaroBrand(getBrandKey());

    const showConsentModal =
        !hideConsentUI &&
        analyticsConsent === null &&
        !consentDismissed &&
        !embedded;

    const showConsentBanner =
        !hideConsentUI &&
        isSuccessLogin &&
        analyticsConsent === null &&
        consentDismissed &&
        !embedded;

    return (
        <>
            <TrackingConsentModal
                open={showConsentModal}
                onClose={handleConsentClose}
                onDismiss={handleConsentClose}
            />
            {showConsentBanner && <ConsentReminderBanner />}
            <Switch>
                <Route path="/initialUpdate" component={InitialUpdate} />
                <Route path="/signin" component={SignIn} />
                <Route
                    path="/callrecording/:callRecordKey"
                    component={CallRecordingPage}
                />
                {isUaePassEnabled && (
                    <Route
                        path={routes.uaePassCallback}
                        component={UaePassWebCallback}
                    />
                )}
                <Route path="/auth/teams" component={TeamsAuthenticationForm} />
                <Route path="/auth/integration/sso" component={CtiSSO} />
                <Route path="/auth/integration" component={CtiAuthentication} />
                <Route path="/release" component={ReleaseNotes} />
                <Route path="/siptest" component={SipTest} />
                <Route path="/auth/:authToken/*" component={AuthWithToken} />
                <Route path="/auth/:authToken" component={AuthWithToken} />
                <Route
                    path={
                        routes[
                            'settings.account.socialintegration.integration.callback'
                        ]
                    }
                    component={SocialIntegrationCallback}
                />
                <Route
                    path="/socialintegration/oauthlanding"
                    component={SocialIntegrationOauthLanding}
                />
                <Route path="/home" component={CentralSwitch} />

                <Route path="/" component={defaultRouteComponent} />
            </Switch>
        </>
    );
};

export default memo(MainRoutes);
